import { useMatomo } from "@jonkoops/matomo-tracker-react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Snackbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import LoadPatientRecordsFromCSV from "../CSVParser";

const LoadPatientInfoDialog = ({
  loadPatientDialogOpen,
  handleLoadPatientDialogClose,
  handleSelectionFromLocalStorage,
  handleUploadedFile,
  fileLoadErrors,
  setFileLoadErrors,
}) => {
  const { trackEvent } = useMatomo();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [keysStored, setKeyStored] = useState([]);
  const [selectedKey, setSelectedKey] = useState([]);
  const [dataSource, setDataSource] = useState("");
  const [fileData, setFileData] = useState([]);
  const [notifyClearAllSuccess, setNotifyClearAllSuccess] = useState(false);

  const handlePatientItemSelect = (value) => () => {
    const currentIndex = selectedKey.indexOf(value);
    const newChecked = [...selectedKey];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setSelectedKey(newChecked);
  };

  const handleClearAllNotificationOpen = () => {
    setNotifyClearAllSuccess(true);
  };

  const handleClearAllNotificationClose = () => {
    setNotifyClearAllSuccess(false);
  };

  const handleClearSavedData = (keysToClear) => {
    let keysStoredCopy = keysStored.slice(0);
    keysToClear.forEach((val) => {
      localStorage.removeItem(`mtx-local-data-${keysStored[val]}`);
      keysStoredCopy.splice(val, 1);
    });
    handleClearAllNotificationOpen();
    setKeyStored(keysStoredCopy);
  };

  useEffect(() => {
    if (selectedKey.length === 1) {
      setDataSource("ls");
    }
  }, [selectedKey]);

  const handleLoadLocalIdentifier = (ls_key) => () => {
    trackEvent({
      category: "Data Entry",
      action: "Load patient data from local storage",
      name: "Load patient data from local storage",
    });
    handleSelectionFromLocalStorage(keysStored[ls_key]);
  };

  const handleLoadFile = () => {
    handleUploadedFile(fileData);
    setDataSource("");
  };

  useEffect(() => {
    if (localStorage.length) {
      setKeyStored(localStorage.length);
      let keys = [];
      for (var i = 0; i < localStorage.length; i++) {
        if (localStorage.key(i).startsWith("mtx-local-data-")) {
          keys.push(localStorage.key(i).split("mtx-local-data-")[1]);
        }
      }
      setKeyStored(keys);
    }
  }, [loadPatientDialogOpen]);

  useEffect(() => {
    if (fileData && fileData.length) {
      setDataSource("file");
      setSelectedKey([]);
    }
  }, [fileData]);

  return (
    <>
      <Snackbar
        open={notifyClearAllSuccess}
        autoHideDuration={6000}
        onClose={handleClearAllNotificationClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleClearAllNotificationClose}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          Saved data cleared successfully.
        </Alert>
      </Snackbar>
      <Dialog
        open={loadPatientDialogOpen}
        onClose={handleLoadPatientDialogClose}
        aria-labelledby="Load Patient Data"
        fullScreen={fullScreen}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 600,
          },
        }}
      >
        <DialogTitle id="load-patient-data-dialog-title">
          Load Patient Data
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", maxWidth: 600 }}>
            <List
              subheader={
                <ListSubheader component="div" id="load-patient-data">
                  Patient Data saved locally
                </ListSubheader>
              }
            >
              {keysStored.length ? (
                keysStored.map((key, index) => (
                  <ListItem
                    key={key}
                    button
                    onClick={handlePatientItemSelect(index)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedKey.indexOf(index) !== -1}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{
                          "aria-labelledby": "patient data local",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={key} />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2">
                  No patient data found in the system
                </Typography>
              )}
            </List>
            <List
              subheader={
                <ListSubheader component="div" id="load-patient-data">
                  Import from file (.csv)
                </ListSubheader>
              }
            >
              <ListItem>
                <ListItemIcon>
                  <AttachFileIcon />
                </ListItemIcon>
                <ListItemText>
                  <LoadPatientRecordsFromCSV
                    infoText="Click to upload patient data"
                    setFileData={setFileData}
                    setFileLoadErrors={setFileLoadErrors}
                    setDataSource={setDataSource}
                  />
                </ListItemText>
              </ListItem>
            </List>
            <Box>
              {fileLoadErrors && fileLoadErrors.length > 0 && (
                <>
                  {fileLoadErrors.map((err, index) => (
                    <FormHelperText key={index} error={true}>
                      {err}
                    </FormHelperText>
                  ))}
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "block",
            px: 3,
            pb: 2,
          }}
        >
          <Button
            color="primary"
            sx={{ px: 2.5, py: 1.2 }}
            onClick={handleLoadPatientDialogClose}
          >
            Cancel
          </Button>
          <Box sx={{ float: "right", display: "inline-block" }}>
            {dataSource === "ls" && selectedKey.length >= 1 && (
              <Tooltip title="Delete selected patient data from local storage">
                <Button
                  color="primary"
                  sx={{ px: 2.5, py: 1.2 }}
                  onClick={() => handleClearSavedData(selectedKey)}
                  disabled={!keysStored.length}
                >
                  Delete
                </Button>
              </Tooltip>
            )}
            {dataSource === "ls" && selectedKey.length === 1 && (
              <Button
                color="primary"
                onClick={handleLoadLocalIdentifier(selectedKey)}
                disabled={!keysStored.length}
                variant="contained"
                sx={{ px: 2.5, py: 1.2, ml: 1 }}
              >
                Load Details
              </Button>
            )}
            {dataSource === "file" && (
              <Button
                color="primary"
                onClick={handleLoadFile}
                sx={{ px: 2.5, py: 1.2, ml: 1 }}
              >
                Load File
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoadPatientInfoDialog;
